import { ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface OrderPreviewMatrixProps {
  previewUrls: ReadonlyArray<string>;
  itemCount?: number | null;
  alt: string;
  className?: string;
  onClick?: () => void;
  ariaLabel?: string;
}

const OrderPreviewMatrix = ({
  previewUrls,
  itemCount,
  alt,
  className,
  onClick,
  ariaLabel,
}: OrderPreviewMatrixProps) => {
  const baseSources = Array.from(new Set(previewUrls.filter((url): url is string => Boolean(url))));
  const totalItems = Math.max(itemCount ?? baseSources.length ?? 1, baseSources.length || 1);
  const gridCells = Array.from({ length: Math.min(totalItems, 4) }, (_, index) => baseSources[index] ?? null);
  const previewGridClass =
    gridCells.length <= 1
      ? 'grid-cols-1'
      : gridCells.length === 2
        ? 'grid-cols-2'
        : 'grid-cols-2 grid-rows-2';
  const extraPreviewCount = Math.max(totalItems - gridCells.length, 0);

  const content = (
    <>
      {gridCells.length > 0 ? (
        <div className={cn('grid h-full w-full gap-0.5', previewGridClass)}>
          {gridCells.map((url, previewIndex) => (
            <div
              key={`preview-${previewIndex}`}
              className="flex h-full w-full items-center justify-center rounded-sm bg-background p-[1px]"
            >
              {url ? (
                <img
                  src={url}
                  alt={alt}
                  className="h-full w-full rounded-[2px] object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center rounded-[2px] bg-muted-foreground/20 text-xs font-semibold text-muted-foreground">
                  {(itemCount ?? 0) > previewIndex ? previewIndex + 1 : ''}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <ImageIcon className="size-6 text-muted-foreground" />
      )}
      {extraPreviewCount > 0 ? (
        <span className="absolute right-1 top-1 rounded-full bg-background/90 px-1.5 py-0.5 text-xs font-semibold text-foreground shadow-sm">
          +{extraPreviewCount}
        </span>
      ) : null}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'group relative flex h-16 w-24 items-center justify-center overflow-hidden rounded-lg border bg-muted text-muted-foreground transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          className,
        )}
        aria-label={ariaLabel}
      >
        {content}
      </button>
    );
  }

  return (
    <div
      className={cn(
        'relative flex h-16 w-24 items-center justify-center overflow-hidden rounded-lg border bg-muted text-muted-foreground',
        className,
      )}
    >
      {content}
    </div>
  );
};

export default OrderPreviewMatrix;
